import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Mic, MicOff, Volume2, RefreshCw, BookOpen, CheckCircle, Sparkles, ChevronRight, Award } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

// ============ ARET'HERA CHARACTER ============
const AretHera = ({ speaking, message }) => (
  <div className="flex items-start gap-4 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
    <div className="relative flex-shrink-0">
      <div 
        className={`w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-3xl ${
          speaking ? 'animate-pulse' : ''
        }`}
        style={{ 
          boxShadow: speaking ? '0 0 30px rgba(139, 92, 246, 0.5)' : '0 0 15px rgba(139, 92, 246, 0.3)'
        }}
      >
        🦉
      </div>
      {speaking && (
        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
          <Volume2 className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
    <div className="flex-1">
      <p className="text-purple-300 font-semibold mb-1">Aret'Hera</p>
      <p className="text-slate-300 text-sm leading-relaxed">{message}</p>
    </div>
  </div>
);

// ============ PASSAGE CARD ============
const PassageCard = ({ passage, onSelect, completed, currentLevel, isLevelUp, isPractice }) => {
  const levelColors = {
    1: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    2: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    3: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
    4: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' }
  };
  
  const colors = levelColors[passage.level] || levelColors[1];
  const isCurrentLevel = passage.level === currentLevel;
  const isChallenge = passage.level > currentLevel;
  
  return (
    <button
      onClick={() => onSelect(passage)}
      className={`p-4 rounded-xl text-left transition-all hover:scale-[1.02] relative ${
        completed 
          ? 'bg-green-500/10 border border-green-500/30' 
          : isChallenge
            ? 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 hover:border-purple-400/50'
            : 'bg-slate-800/50 border border-slate-700/50 hover:border-purple-500/50'
      }`}
      data-testid={`passage-${passage.id}`}
    >
      {/* Challenge/Level Up Badge */}
      {isChallenge && !completed && (
        <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-[10px] text-white font-bold">
          ✨ Challenge!
        </div>
      )}
      
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">
            {passage.category === 'adventure' ? '🗺️' : 
             passage.category === 'nature' ? '🌿' : '✨'}
          </span>
          <div>
            <h3 className="text-white font-semibold">{passage.title}</h3>
            <p className="text-slate-500 text-xs capitalize">{passage.category.replace('_', ' ')}</p>
          </div>
        </div>
        {completed && (
          <CheckCircle className="w-5 h-5 text-green-400" />
        )}
      </div>
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className={`px-2 py-1 rounded-full ${colors.bg} ${colors.text}`}>
          {passage.level_name || passage.difficulty}
        </span>
        <span className="text-slate-500">{passage.word_count} words</span>
        {isCurrentLevel && (
          <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 text-[10px]">
            Your Level
          </span>
        )}
      </div>
    </button>
  );
};

// ============ WORD HIGHLIGHT COMPONENT ============
// Color meanings: Green = correct, Yellow = let's try again, Gray = we'll practice this one
const WordDisplay = ({ word, status }) => {
  let className = "inline-block px-1.5 py-0.5 mx-0.5 rounded transition-all ";
  
  if (status === 'correct') {
    // Green - read correctly
    className += "bg-green-500/25 text-green-300 border border-green-500/30";
  } else if (status === 'misread') {
    // Yellow - uncertain/let's try again (supportive, not punitive)
    className += "bg-amber-500/25 text-amber-300 border border-amber-500/30";
  } else if (status === 'skipped') {
    // Gray - skipped word, we'll practice this one
    className += "bg-slate-500/20 text-slate-400 opacity-80";
  } else if (status === 'current') {
    // Purple pulse - currently reading
    className += "bg-purple-500/30 text-purple-200 ring-2 ring-purple-400 animate-pulse";
  } else {
    // Default - not yet read
    className += "text-slate-300";
  }
  
  return <span className={className}>{word}</span>;
};

// ============ MAIN COMPONENT ============
const TempleListens = ({ selectedChild, onClose }) => {
  // States
  const [passages, setPassages] = useState([]);
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [comparisonResults, setComparisonResults] = useState(null);
  const [completedPassages, setCompletedPassages] = useState([]);
  const [aretHeraMessage, setAretHeraMessage] = useState("Hello there! I'm Aret'Hera, and I love listening to stories. Pick a story below, and when you're ready, press the purple button and read it aloud to me!");
  const [aretHeraSpeaking, setAretHeraSpeaking] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterLevel, setFilterLevel] = useState(null);
  
  // Level & Progress state
  const [currentLevel, setCurrentLevel] = useState(1);
  const [levelRecommendation, setLevelRecommendation] = useState(null);
  const [showLevelUpCelebration, setShowLevelUpCelebration] = useState(false);
  const [levelsInfo, setLevelsInfo] = useState({});
  
  // Live word highlighting state
  const [highlightedWords, setHighlightedWords] = useState([]); // Array of { index, status: 'correct'|'current' }
  const [liveTranscription, setLiveTranscription] = useState(''); // What we've heard so far
  
  // Refs for audio recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const readingStartTimeRef = useRef(null); // Track reading duration
  const streamRef = useRef(null); // Store stream for cleanup
  const transcriptionIntervalRef = useRef(null); // Interval for streaming transcription
  const matchedWordsRef = useRef(new Set()); // Track which word indices have been matched
  const lastTranscriptionRef = useRef(''); // Track cumulative transcription
  
  // Load passages and progress on mount
  useEffect(() => {
    loadPassages();
    if (selectedChild) {
      loadReadingProgress();
    }
  }, [filterCategory, filterLevel, selectedChild]);
  
  const loadPassages = async () => {
    try {
      let url = '/temple-listens/passages';
      const params = [];
      if (filterCategory) params.push(`category=${filterCategory}`);
      if (filterLevel) params.push(`level=${filterLevel}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await api.get(url);
      setPassages(response.data.passages);
      setLevelsInfo(response.data.levels || {});
    } catch (error) {
      console.error('Error loading passages:', error);
      toast.error('Could not load reading passages');
    }
  };
  
  const loadReadingProgress = async () => {
    try {
      const response = await api.get(`/temple-listens/progress/${selectedChild.id}`);
      const data = response.data;
      
      // Set current level from progress
      if (data.summary?.current_level_num) {
        setCurrentLevel(data.summary.current_level_num);
      }
      
      // Set level recommendation with encouraging message
      if (data.level_recommendation) {
        setLevelRecommendation(data.level_recommendation);
        
        // If ready to level up, show encouraging message
        if (data.level_recommendation.ready_for_next_level) {
          setAretHeraMessage(data.level_recommendation.message);
          setAretHeraSpeaking(true);
          setTimeout(() => setAretHeraSpeaking(false), 3000);
        }
      }
    } catch (error) {
      console.log('No reading progress yet');
      setCurrentLevel(1);
      setLevelRecommendation(null);
    }
  };
  
  // Normalize word for comparison (remove punctuation, lowercase)
  const normalizeWord = (word) => {
    return word.toLowerCase().replace(/[^a-z0-9]/g, '');
  };
  
  // Match transcribed text against passage and update highlights
  const matchAndHighlight = (transcribedText) => {
    if (!selectedPassage || !transcribedText) return;
    
    const passageWords = selectedPassage.text.split(/\s+/);
    const transcribedWords = transcribedText.split(/\s+/).map(normalizeWord).filter(w => w.length > 0);
    
    // Find matches - be forgiving for children's speech
    const newHighlights = [];
    let transcribedIndex = 0;
    
    for (let i = 0; i < passageWords.length; i++) {
      const passageWord = normalizeWord(passageWords[i]);
      
      // Skip very short words for matching (like "a", "I")
      if (passageWord.length <= 1) {
        // Check if we're past this word in transcription
        if (transcribedIndex > 0) {
          newHighlights.push({ index: i, status: 'correct' });
        }
        continue;
      }
      
      // Look ahead in transcription to find this word
      let found = false;
      for (let j = transcribedIndex; j < transcribedWords.length && j < transcribedIndex + 5; j++) {
        const transcribedWord = transcribedWords[j];
        
        // Fuzzy match - check if words are similar enough
        if (passageWord === transcribedWord || 
            (passageWord.length > 3 && transcribedWord.includes(passageWord.substring(0, 3))) ||
            (transcribedWord.length > 3 && passageWord.includes(transcribedWord.substring(0, 3)))) {
          found = true;
          transcribedIndex = j + 1;
          break;
        }
      }
      
      if (found || matchedWordsRef.current.has(i)) {
        matchedWordsRef.current.add(i);
        newHighlights.push({ index: i, status: 'correct' });
      }
    }
    
    // Mark the next unmatched word as "current" (where child should be reading)
    const nextUnmatched = passageWords.findIndex((_, i) => !matchedWordsRef.current.has(i));
    if (nextUnmatched !== -1 && nextUnmatched < passageWords.length) {
      const existingIndex = newHighlights.findIndex(h => h.index === nextUnmatched);
      if (existingIndex === -1) {
        newHighlights.push({ index: nextUnmatched, status: 'current' });
      }
    }
    
    setHighlightedWords(newHighlights);
    setCurrentWordIndex(nextUnmatched);
  };
  
  // Send audio chunk for transcription
  const sendAudioChunk = async () => {
    if (audioChunksRef.current.length === 0) return;
    
    try {
      // Create blob from all accumulated chunks
      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
      
      // Only send if we have enough audio (at least 500ms worth)
      if (audioBlob.size < 5000) return;
      
      const formData = new FormData();
      const ext = mimeType.split('/')[1] || 'webm';
      formData.append('file', audioBlob, `chunk.${ext}`);
      
      const response = await api.post('/temple-listens/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success && response.data.transcription) {
        const newTranscription = response.data.transcription;
        
        // Only update if we got more content
        if (newTranscription.length >= lastTranscriptionRef.current.length) {
          lastTranscriptionRef.current = newTranscription;
          setLiveTranscription(newTranscription);
          matchAndHighlight(newTranscription);
        }
      }
    } catch (error) {
      console.log('Streaming transcription chunk error:', error.message);
    }
  };
  
  // Start recording with live streaming
  const startRecording = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setAretHeraMessage("Oh no! Your browser doesn't support microphone access. Try using Chrome, Safari, or Firefox.");
        toast.error('Microphone not supported in this browser');
        return;
      }
      
      // Request microphone permission - this triggers the browser's permission popup
      setAretHeraMessage("Please allow microphone access when your browser asks. I'm excited to hear you read!");
      setAretHeraSpeaking(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Permission granted - update message
      setAretHeraMessage("Great! I can hear you now. Start reading whenever you're ready!");
      setTimeout(() => setAretHeraSpeaking(false), 1500);
      
      // Detect supported mimeType
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      // Reset state
      audioChunksRef.current = [];
      matchedWordsRef.current = new Set();
      lastTranscriptionRef.current = '';
      setHighlightedWords([]);
      setLiveTranscription('');
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Clear the streaming interval
        if (transcriptionIntervalRef.current) {
          clearInterval(transcriptionIntervalRef.current);
          transcriptionIntervalRef.current = null;
        }
        
        // Final processing with full audio
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processRecording(audioBlob, mimeType);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      
      // Start recording with timeslice to get data frequently
      mediaRecorder.start(500); // Get data every 500ms
      setIsRecording(true);
      setCurrentWordIndex(0);
      readingStartTimeRef.current = Date.now();
      
      // Start streaming transcription every 2 seconds
      transcriptionIntervalRef.current = setInterval(sendAudioChunk, 2000);
      
      // Aret'Hera encouragement - warm and supportive
      setAretHeraMessage("I'm listening! Watch the words light up as you read. Take your time!");
      setAretHeraSpeaking(true);
      setTimeout(() => setAretHeraSpeaking(false), 2000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      
      // Handle different error types with helpful messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // User denied microphone permission
        setAretHeraMessage(
          "I need microphone access to hear you read! Here's how to enable it:\n\n" +
          "• Look for a microphone icon in your browser's address bar\n" +
          "• Click it and select 'Allow'\n" +
          "• Then press the microphone button again!"
        );
        toast.error('Please allow microphone access to read aloud. Look for the permission popup or click the microphone icon in your browser.');
      } else if (error.name === 'NotFoundError') {
        // No microphone device found
        setAretHeraMessage("I can't find a microphone on your device. Make sure one is connected and try again!");
        toast.error('No microphone found. Please connect a microphone.');
      } else if (error.name === 'NotReadableError' || error.name === 'AbortError') {
        // Microphone is busy or hardware error
        setAretHeraMessage("Your microphone seems busy. Try closing other apps that might be using it, then try again!");
        toast.error('Microphone is in use by another app. Please close it and try again.');
      } else {
        // Generic error
        setAretHeraMessage("Hmm, I couldn't connect to your microphone. Let's check your settings and try again!");
        toast.error('Could not access microphone. Please check your browser settings.');
      }
      setAretHeraSpeaking(true);
      setTimeout(() => setAretHeraSpeaking(false), 4000);
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Clear the streaming interval first
      if (transcriptionIntervalRef.current) {
        clearInterval(transcriptionIntervalRef.current);
        transcriptionIntervalRef.current = null;
      }
      
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);
      setAretHeraMessage("Let me see how you did...");
      setAretHeraSpeaking(true);
    }
  };
  
  // Process the recording
  const processRecording = async (audioBlob, mimeType = 'audio/webm') => {
    try {
      const formData = new FormData();
      // Determine file extension from mimeType
      const ext = mimeType.split('/')[1] || 'webm';
      formData.append('file', audioBlob, `recording.${ext}`);
      
      // Transcribe
      const transcribeResponse = await api.post('/temple-listens/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (transcribeResponse.data.success) {
        const text = transcribeResponse.data.transcription;
        setTranscription(text);
        
        // Compare with passage - send transcription in request body for long texts
        const compareResponse = await api.post('/temple-listens/compare', null, {
          params: {
            passage_id: selectedPassage.id,
            transcription: text
          }
        });
        setComparisonResults(compareResponse.data);
        
        // Update Aret'Hera message based on results - supportive and encouraging
        const accuracy = compareResponse.data.accuracy;
        setAretHeraSpeaking(true);
        
        if (accuracy >= 90) {
          setAretHeraMessage("Wonderful reading! I loved listening to your story! You're becoming such a great reader!");
        } else if (accuracy >= 70) {
          setAretHeraMessage("That was lovely! I really enjoyed listening to you read.");
          if (compareResponse.data.misread_words.length > 0) {
            setTimeout(() => {
              setAretHeraMessage(`The yellow words are ones we can practice together. Let's try "${compareResponse.data.misread_words[0]}" again when you're ready!`);
            }, 3000);
          }
        } else if (accuracy >= 50) {
          setAretHeraMessage("Thank you for reading to me! Some words are tricky, but that's okay. Want to try again together?");
        } else {
          setAretHeraMessage("I enjoyed listening! This passage has some challenging words. Let's read it together - I'll be right here with you!");
        }
        
        // Record progress - always record for parent visibility, not just when passed
        if (selectedChild) {
          const timeSpent = readingStartTimeRef.current 
            ? Math.round((Date.now() - readingStartTimeRef.current) / 1000) 
            : 0;
          
          await api.post(`/temple-listens/progress/${selectedChild.id}`, null, {
            params: {
              passage_id: selectedPassage.id,
              accuracy: accuracy,
              misread_words: compareResponse.data.misread_words || [],
              time_spent_seconds: timeSpent
            }
          });
          
          if (accuracy >= 70) {
            setCompletedPassages(prev => [...prev, selectedPassage.id]);
            toast.success('+10 Discovery Sparks!');
            
            // Check if we should level up after this reading
            setTimeout(async () => {
              try {
                const progressRes = await api.get(`/temple-listens/progress/${selectedChild.id}`);
                const newRecommendation = progressRes.data.level_recommendation;
                
                // If newly ready to level up, celebrate!
                if (newRecommendation?.ready_for_next_level && 
                    (!levelRecommendation?.ready_for_next_level || 
                     newRecommendation.recommended_level > levelRecommendation.recommended_level)) {
                  setShowLevelUpCelebration(true);
                  setLevelRecommendation(newRecommendation);
                  
                  setTimeout(() => {
                    setShowLevelUpCelebration(false);
                    setAretHeraMessage(newRecommendation.message);
                    setAretHeraSpeaking(true);
                    setTimeout(() => setAretHeraSpeaking(false), 3000);
                  }, 3000);
                } else {
                  setLevelRecommendation(newRecommendation);
                }
              } catch (err) {
                console.log('Could not check level progress');
              }
            }, 1000);
          }
        }
        
        setTimeout(() => setAretHeraSpeaking(false), 2000);
      }
      
    } catch (error) {
      console.error('Error processing recording:', error);
      setAretHeraMessage("I had a little trouble hearing that. Don't worry - would you like to try again? I'm ready when you are!");
      setAretHeraSpeaking(true);
      setTimeout(() => setAretHeraSpeaking(false), 3000);
      toast.error('Let\'s try that again - I want to hear your reading!');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Reset reading
  const resetReading = () => {
    setTranscription('');
    setComparisonResults(null);
    setCurrentWordIndex(-1);
    setAretHeraMessage("I'd love to hear that again! Press the purple button when you're ready to read.");
  };
  
  // Go back to passage selection
  const goBackToSelection = () => {
    setSelectedPassage(null);
    setComparisonResults(null);
    setTranscription('');
    setAretHeraMessage("What story would you like to read to me next?");
  };
  
  // ============ RENDER PASSAGE SELECTION ============
  const renderPassageSelection = () => {
    // Group passages by level for display
    const passagesByLevel = passages.reduce((acc, p) => {
      const level = p.level || 1;
      if (!acc[level]) acc[level] = [];
      acc[level].push(p);
      return acc;
    }, {});
    
    const levelNames = {
      1: { name: 'Beginner Reader', icon: '🌱', color: 'emerald' },
      2: { name: 'Growing Reader', icon: '🌿', color: 'blue' },
      3: { name: 'Confident Reader', icon: '🌳', color: 'purple' },
      4: { name: 'Advanced Reader', icon: '⭐', color: 'amber' }
    };
    
    return (
      <div className="space-y-6">
        {/* Aret'Hera greeting */}
        <AretHera speaking={aretHeraSpeaking} message={aretHeraMessage} />
        
        {/* Current Level Display - Encouraging */}
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{levelNames[currentLevel]?.icon || '🌱'}</span>
              <div>
                <p className="text-white font-semibold">{levelNames[currentLevel]?.name || 'Beginner Reader'}</p>
                <p className="text-purple-300 text-sm">Your current reading level</p>
              </div>
            </div>
            
            {/* Level recommendation badge */}
            {levelRecommendation?.ready_for_next_level && (
              <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full animate-pulse">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-semibold">Ready for a challenge!</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Category filters */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterCategory(null)}
            className={`px-4 py-2 rounded-full text-sm transition-all ${
              !filterCategory 
                ? 'bg-purple-500 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            All Stories
          </button>
          {['adventure', 'nature', 'fairy_tale'].map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm transition-all capitalize ${
                filterCategory === cat 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {cat === 'adventure' ? '🗺️' : cat === 'nature' ? '🌿' : '✨'} {cat.replace('_', ' ')}
            </button>
          ))}
        </div>
        
        {/* Level Filter Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-slate-700/50 pb-4">
          <button
            onClick={() => setFilterLevel(null)}
            className={`px-4 py-2 rounded-lg text-sm transition-all ${
              !filterLevel 
                ? 'bg-slate-700 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            All Levels
          </button>
          {[1, 2, 3, 4].map(level => (
            <button
              key={level}
              onClick={() => setFilterLevel(level)}
              className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                filterLevel === level 
                  ? 'bg-slate-700 text-white' 
                  : level === currentLevel
                    ? 'text-purple-400 hover:text-purple-300 font-medium'
                    : 'text-slate-400 hover:text-white'
              }`}
            >
              {levelNames[level].icon} {levelNames[level].name}
              {level === currentLevel && <span className="text-xs">(You)</span>}
            </button>
          ))}
        </div>
        
        {/* Passages - Show current level first, then others */}
        {!filterLevel ? (
          // Show all levels, organized
          <div className="space-y-8">
            {/* Current Level - Your Stories */}
            {passagesByLevel[currentLevel]?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  {levelNames[currentLevel]?.icon} Stories for You
                  <span className="text-xs px-2 py-0.5 bg-purple-500/30 text-purple-300 rounded-full">Your Level</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {passagesByLevel[currentLevel].map(passage => (
                    <PassageCard 
                      key={passage.id}
                      passage={passage}
                      onSelect={setSelectedPassage}
                      completed={completedPassages.includes(passage.id)}
                      currentLevel={currentLevel}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Next Level - Challenge Stories */}
            {currentLevel < 4 && passagesByLevel[currentLevel + 1]?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  {levelNames[currentLevel + 1]?.icon} Challenge Stories
                  {levelRecommendation?.ready_for_next_level && (
                    <span className="text-xs px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full">
                      ✨ Ready!
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {passagesByLevel[currentLevel + 1].map(passage => (
                    <PassageCard 
                      key={passage.id}
                      passage={passage}
                      onSelect={setSelectedPassage}
                      completed={completedPassages.includes(passage.id)}
                      currentLevel={currentLevel}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Practice Stories - One level below */}
            {currentLevel > 1 && passagesByLevel[currentLevel - 1]?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-400 mb-3 flex items-center gap-2">
                  {levelNames[currentLevel - 1]?.icon} Practice & Review
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-80">
                  {passagesByLevel[currentLevel - 1].map(passage => (
                    <PassageCard 
                      key={passage.id}
                      passage={passage}
                      onSelect={setSelectedPassage}
                      completed={completedPassages.includes(passage.id)}
                      currentLevel={currentLevel}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Filtered view - just show the selected level
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {passages.map(passage => (
              <PassageCard 
                key={passage.id}
                passage={passage}
                onSelect={setSelectedPassage}
                completed={completedPassages.includes(passage.id)}
                currentLevel={currentLevel}
              />
            ))}
          </div>
        )}
        
        {passages.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No passages found</p>
          </div>
        )}
      </div>
    );
  };
  
  // ============ RENDER READING SCREEN ============
  const renderReadingScreen = () => {
    const words = selectedPassage.text.split(/\s+/);
    
    return (
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={goBackToSelection}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Choose different story
        </button>
        
        {/* Aret'Hera feedback */}
        <AretHera speaking={aretHeraSpeaking} message={aretHeraMessage} />
        
        {/* Passage display */}
        <div className="bg-slate-900/50 rounded-2xl border border-slate-700/50 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">
              {selectedPassage.category === 'adventure' ? '🗺️' : 
               selectedPassage.category === 'nature' ? '🌿' : '✨'}
            </span>
            <div>
              <h2 className="text-xl font-bold text-white">{selectedPassage.title}</h2>
              <p className="text-slate-500 text-sm capitalize">{selectedPassage.category} • {selectedPassage.difficulty}</p>
            </div>
          </div>
          
          {/* Text with word highlighting */}
          <div className="text-lg md:text-xl leading-relaxed space-y-2">
            {comparisonResults ? (
              // Show comparison results after recording
              <div className="flex flex-wrap gap-1">
                {comparisonResults.word_results.map((result, idx) => (
                  <WordDisplay 
                    key={idx} 
                    word={result.word} 
                    status={result.status}
                  />
                ))}
              </div>
            ) : isRecording || isProcessing ? (
              // Live word highlighting during recording
              <div className="flex flex-wrap gap-1">
                {words.map((word, idx) => {
                  // Find this word's status from highlightedWords
                  const highlight = highlightedWords.find(h => h.index === idx);
                  let status = null;
                  
                  if (highlight) {
                    status = highlight.status;
                  } else if (idx === currentWordIndex) {
                    status = 'current';
                  }
                  
                  return (
                    <WordDisplay 
                      key={idx} 
                      word={word} 
                      status={status}
                    />
                  );
                })}
              </div>
            ) : (
              // Default view before recording starts
              <div className="flex flex-wrap gap-1">
                {words.map((word, idx) => (
                  <WordDisplay 
                    key={idx} 
                    word={word} 
                    status={null}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Live transcription preview during recording */}
          {isRecording && liveTranscription && (
            <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl">
              <p className="text-xs text-purple-400 mb-1">What I'm hearing:</p>
              <p className="text-sm text-purple-300 italic">"{liveTranscription}"</p>
            </div>
          )}
        </div>
        
        {/* Results panel - supportive, not test-like */}
        {comparisonResults && (
          <div className={`rounded-xl p-6 border ${
            comparisonResults.accuracy >= 70 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {comparisonResults.accuracy >= 70 ? (
                  <Award className="w-8 h-8 text-green-400" />
                ) : (
                  <Sparkles className="w-8 h-8 text-amber-400" />
                )}
                <div>
                  <p className="text-white font-bold text-lg">
                    {comparisonResults.accuracy >= 90 
                      ? "Amazing Reading!" 
                      : comparisonResults.accuracy >= 70 
                        ? "Great Job!" 
                        : "Nice Try!"}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {comparisonResults.accuracy >= 70 
                      ? `${comparisonResults.correct_count} words read beautifully!`
                      : "Let's practice the yellow words together"}
                  </p>
                </div>
              </div>
              
              {comparisonResults.accuracy >= 70 && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/40">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-300 font-semibold">+10 Sparks!</span>
                </div>
              )}
            </div>
            
            {/* Words to practice - gentle phrasing */}
            {comparisonResults.misread_words.length > 0 && (
              <div className="mt-4">
                <p className="text-slate-400 text-sm mb-2">Let's try these words again:</p>
                <div className="flex flex-wrap gap-2">
                  {comparisonResults.misread_words.map((word, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-sm border border-amber-500/30"
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Controls */}
        <div className="flex justify-center gap-4">
          {!comparisonResults ? (
            <>
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold transition-all ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                    : isProcessing
                      ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                      : 'bg-purple-500 hover:bg-purple-600 text-white'
                }`}
                data-testid="record-btn"
              >
                {isProcessing ? (
                  <>
                    <RefreshCw className="w-6 h-6 animate-spin" />
                    Processing...
                  </>
                ) : isRecording ? (
                  <>
                    <MicOff className="w-6 h-6" />
                    Stop Reading
                  </>
                ) : (
                  <>
                    <Mic className="w-6 h-6" />
                    Start Reading
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={resetReading}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-semibold transition-all"
                data-testid="try-again-btn"
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </button>
              
              <button
                onClick={goBackToSelection}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-semibold transition-all"
              >
                <ChevronRight className="w-5 h-5" />
                Next Story
              </button>
            </>
          )}
        </div>
        
        {/* Recording indicator - friendly */}
        {isRecording && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 px-6 py-3 rounded-full bg-purple-600/90 text-white shadow-lg backdrop-blur-sm border border-purple-400/30">
            <div className="w-3 h-3 bg-white rounded-full animate-ping" />
            <span className="font-medium">Reading aloud... Take your time!</span>
          </div>
        )}
      </div>
    );
  };
  
  // ============ MAIN RENDER ============
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950/20 to-slate-900 p-4 md:p-8">
      {/* Level Up Celebration Overlay */}
      {showLevelUpCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="text-center animate-bounce">
            <div className="text-8xl mb-6">🎉</div>
            <h2 className="text-4xl font-bold text-white mb-4">Amazing Progress!</h2>
            <p className="text-2xl text-purple-300 mb-2">You're ready for new challenges!</p>
            <div className="flex items-center justify-center gap-4 mt-6">
              <Sparkles className="w-8 h-8 text-amber-400 animate-pulse" />
              <span className="text-xl text-amber-400 font-semibold">Keep Growing!</span>
              <Sparkles className="w-8 h-8 text-amber-400 animate-pulse" />
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Courtyard
          </button>
          
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold text-white">Temple Listens</h1>
          </div>
          
          {selectedChild && (
            <div className="text-slate-400 text-sm">
              Reading with {selectedChild.name}
            </div>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto">
        {selectedPassage ? renderReadingScreen() : renderPassageSelection()}
      </div>
    </div>
  );
};

export default TempleListens;
